import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuOpsListToolbar from '@/temu-platform/components/TemuOpsListToolbar';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import TemuOpsCopyHoverMenu from '@/temu-platform/components/TemuOpsCopyHoverMenu';
import TemuTemplateScopeTabs from '@/temu-platform/components/TemuTemplateScopeTabs';
import TemuStyleNoBatchFilter from '@/temu-platform/components/TemuStyleNoBatchFilter';
import TemuTemplateNameBatchFilter from '@/temu-platform/components/TemuTemplateNameBatchFilter';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuImagePreviewLightbox from '@/temu-platform/modules/products/components/TemuImagePreviewLightbox';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import PhotoTemplateCreateModal from '@/temu-platform/modules/templates/PhotoTemplateCreateModal';
import useMaterialTemplatePortableActions from '@/temu-platform/modules/templates/useMaterialTemplatePortableActions';
import { formatBeijingDateTime } from '@/temu-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/temu-platform/utils/cellDisplayTitle';
import { copyTemplateName } from '@/temu-platform/utils/temuTemplateCopyName';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { MATERIAL_TEMPLATE_TYPES } from '@/temu-platform/utils/temuMaterialTemplatePortable';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { matchTemplateName, templateNameTokens } from '@/temu-platform/utils/templateNameQuery';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import {
  buildTemuWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  resolveCopySourceScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  rowCanDeleteScopedTemplate,
  rowCanEditScopedTemplate,
  templateCopiedToast,
} from '@/temu-platform/constants/temuTemplateScope';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import useTemuTemplateScope from '@/temu-platform/hooks/useTemuTemplateScope';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { usePlatformShopPick, resolveWriteShopId } from '@/hooks/usePlatformShopPick';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function rowKey(row) {
  return String(row?.id ?? '');
}

const COL_SPAN_FULL = 12;
const COL_SPAN_OFFICIAL = 11;

function PreviewThumb({ src, alt, onPreview }) {
  const { t } = useTranslation('workbench');
  if (!src) {
    return <span className="tpo-template-thumb tpo-template-thumb--empty" aria-hidden="true">—</span>;
  }
  return (
    <button
      type="button"
      className="tpo-template-thumbs__btn"
      title={t('photoLib.previewClick')}
      onClick={() => onPreview?.(src, alt)}
    >
      <img className="tpo-template-thumb" src={src} alt={alt || ''} />
    </button>
  );
}

/** 模板库 · 实拍图库（对齐证书库列表布局） */
export default function PhotoTemplatePage() {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [] } = useTemuPageShopScope();
  const {
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
    categoryOptions,
    categories,
  } = useTemuTemplateScope({ preferredShopId: pageShopScope });

  
  
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => resolveWriteShopId({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    title,
    description,
    confirmText,
  }), [listAllShops, pageShopScope, listShopId, pickShop, t]);

const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [styleNoDraft, setStyleNoDraft] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingShopId, setEditingShopId] = useState('');
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [copyingId, setCopyingId] = useState(null);
  const [batchCopying, setBatchCopying] = useState(false);
  const { isBusy } = useTemuSyncJobs();
  const photoBatchCopying = isBusy('photoTemplateBatchCopy', listShopId) || batchCopying;
  const [moving, setMoving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');
  const selectedRowsRef = useRef(new Map());
  const modalShopId = editingShopId || listShopId;
  const tableColSpan = isOfficialTab ? COL_SPAN_OFFICIAL : COL_SPAN_FULL;

  const load = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setItems([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const nameTokens = templateNameTokens(keyword);
      const baseParams = {
        scope: scopeTab,
        styleNo: styleNo || undefined,
        productTypeId: productTypeId || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      };
      if (nameTokens.length > 1) {
        const all = await fetchAllPages(async (pageNo, size) => {
          const res = await temuPlatformAPI.listPhotoTemplates(listShopId, {
            ...baseParams,
            page: pageNo,
            pageSize: size,
          });
          return {
            items: Array.isArray(res?.items) ? res.items : [],
            total: Number(res?.total) || 0,
          };
        });
        const filtered = all.filter((row) => matchTemplateName(row.name, nameTokens));
        const start = (Math.max(1, page) - 1) * pageSize;
        setItems(filtered.slice(start, start + pageSize));
        setTotal(filtered.length);
      } else {
        const res = await temuPlatformAPI.listPhotoTemplates(listShopId, {
          ...baseParams,
          keyword: nameTokens[0] || undefined,
          page,
          pageSize,
        });
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total) || 0);
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('productTemplate.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId, page, pageSize, queryNonce, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedKeys.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
  }, [items, selectedKeys]);

  const selectableKeys = useMemo(
    () => items
      .filter((row) => rowCanDeleteScopedTemplate(row) || rowCanCopyScopedTemplate(row))
      .map(rowKey)
      .filter(Boolean),
    [items],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCategoryChange = (id, meta) => {
    const nextCategoryId = id ? String(id) : '';
    const hit = categoryOptions.find((opt) => String(opt.value) === nextCategoryId);
    // 类目缓存只存一级，末级由级联懒加载返回，categoryOptions 里可能查不到 → 用 meta 兜底
    const metaTypeId = meta?.productTypeId != null && String(meta.productTypeId) !== '0'
      ? String(meta.productTypeId)
      : '';
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : (metaTypeId || nextCategoryId);
    setCategoryIdDraft(nextCategoryId);
    setProductTypeIdDraft(nextTypeId);
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setStyleNo(styleNoDraft.trim());
    setProductTypeId(productTypeIdDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setStyleNoDraft('');
    setStyleNo('');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setProductTypeId('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resolveSelectedRows = useCallback(() => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  }, [selectedKeys]);

  const resolvePageRows = useCallback(() => items, [items]);

  const resolveFilteredRows = useCallback(async () => {
    if (!canQuery || !listShopId) return [];
    const nameTokens = templateNameTokens(keyword);
    const baseParams = {
      scope: scopeTab,
      styleNo: styleNo || undefined,
      productTypeId: productTypeId || undefined,
      ...(listAllShops ? { allShops: true } : {}),
    };
    if (nameTokens.length > 1) {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await temuPlatformAPI.listPhotoTemplates(listShopId, {
          ...baseParams,
          page: pageNo,
          pageSize: size,
        });
        return {
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total) || 0,
        };
      });
      return all.filter((row) => matchTemplateName(row.name, nameTokens));
    }
    return fetchAllPages(async (pageNo, size) => {
      const res = await temuPlatformAPI.listPhotoTemplates(listShopId, {
        ...baseParams,
        keyword: nameTokens[0] || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId]);

  const {
    exporting,
    onExportCsvFiltered,
    onExportCsvPage,
    onExportCsvSelected,
  } = useMaterialTemplatePortableActions({
    type: MATERIAL_TEMPLATE_TYPES.photos,
    shopId: listShopId,
    filePrefix: t('photoLib.filePrefix'),
    resolvePageRows,
    resolveSelectedRows,
    resolveFilteredRows,
    buildCsvRows: (rows) => rows.map((row) => ({
      [t('productTemplate.colPriority')]: row.sortOrder ?? 0,
      [t('productTemplate.colStyleNo')]: row.styleNo || '',
      [t('productTemplate.colName')]: row.name || '',
      [t('productTemplate.colCategory')]: row.categoryPathLabel || row.productTypeId || '',
      SPU: row.spuName || '',
      [t('photoLib.productName')]: row.spuTitle || '',
      [t('productTemplate.colAutoRule')]: row.autoRuleEnabled ? t('productTemplate.on') : t('productTemplate.off'),
      [t('photoLib.packageCount')]: row.packageCount ?? 0,
      [t('photoLib.bodyCount')]: row.bodyCount ?? 0,
      [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.updatedAt),
    })),
    getOne: (id) => temuPlatformAPI.getPhotoTemplate(listShopId, id, { scope: scopeTab }),
    listByProductType: async (productTypeId, ctx) => {
      const sid = ctx?.shopId || listShopId;
      const res = await temuPlatformAPI.listPhotoTemplates(sid, {
        scope: scopeTab,
        productTypeId: productTypeId || undefined,
        page: 1,
        pageSize: 200,
      });
      return Array.isArray(res?.items) ? res.items : [];
    },
    createOne: (body, ctx) => temuPlatformAPI.createPhotoTemplate(ctx?.shopId || listShopId, {
      ...body,
      ...(body.scope ? {} : { scope: scopeTab }),
    }),
    onImported: async () => {
      setSelectedKeys(new Set());
      setPage(1);
      setQueryNonce((n) => n + 1);
    },
  });

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdDelete'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('photoLib.batchDeleteMsg', { count: ids.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeletePhotoTemplates(listShopId, ids);
      const deleted = Number(res?.deleted);
      showToast(t('productTemplate.deletedCount', { count: Number.isFinite(deleted) ? deleted : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    setBatchCopying(true);
    try {
      const res = await temuPlatformAPI.batchCopyPhotoTemplates(writeShopId, {
        ids,
        sourceScope: resolveCopySourceScope(null, { isOfficialTab, scopeTab }),
        targetScope,
      });
      const copied = Number(res?.copied) || 0;
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      showToast(t('productTemplate.copiedCount', { count: copied > 0 ? copied : ids.length }), 'success');
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchCopyFailed'), 'error');
    } finally {
      setBatchCopying(false);
    }
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdMove'), 'error');
      return;
    }
    setMoving(true);
    try {
      const res = await temuPlatformAPI.movePhotoTemplates(listShopId, {
        ids,
        targetScope,
      });
      const moved = Number(res?.moved);
      showToast(t('productTemplate.movedCount', { count: Number.isFinite(moved) ? moved : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  const onDeleteOne = async (row) => {
    if (!canQuery || !row?.id || !rowCanDeleteScopedTemplate(row)) return;
    const ok = await showTemuConfirm({
      title: t('productTemplate.deleteTitle'),
      message: t('productTemplate.deleteOne', { name: row.name || row.id }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await temuPlatformAPI.deletePhotoTemplate(resolveTemplateRowShopId(row, listShopId), row.id);
      showToast(t('productTemplate.deleted'), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onCopyOne = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId) return;
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await temuPlatformAPI.copyPhotoTemplate(writeShopId, {
        sourceId: row.id,
        sourceScope: resolveCopySourceScope(row, { isOfficialTab, scopeTab }),
        targetScope: resolvedTarget,
        name: copyTemplateName(row.name),
      });
      showToast(templateCopiedToast(resolvedTarget), 'success');
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onToggleAutoRule = async (row, enabled) => {
    if (!canQuery || !row?.id || !rowCanEditScopedTemplate(row)) return;
    const id = row.id;
    const key = String(id);
    const prevEnabled = row.autoRuleEnabled === true || row.autoRuleEnabled === 1;
    setTogglingIds((prev) => new Set(prev).add(key));
    setItems((prev) => prev.map((item) => (
      String(item.id) === key ? { ...item, autoRuleEnabled: enabled } : item
    )));
    try {
      const body = { autoRuleEnabled: enabled };
      const version = parseTemplateVersion(row.version);
      if (version != null) body.version = version;
      const res = await temuPlatformAPI.updatePhotoTemplate(resolveTemplateRowShopId(row, listShopId), id, body);
      const nextVersion = parseTemplateVersion(res?.item?.version);
      setItems((prev) => prev.map((item) => (
        String(item.id) === key
          ? {
            ...item,
            autoRuleEnabled: enabled,
            ...(nextVersion != null ? { version: nextVersion } : {}),
          }
          : item
      )));
      showToast(enabled ? t('productTemplate.autoOn') : t('productTemplate.autoOff'), 'success');
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        String(item.id) === key ? { ...item, autoRuleEnabled: prevEnabled } : item
      )));
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'photo' }]), 'warning', 3000);
        try {
          const res = await temuPlatformAPI.getPhotoTemplate(resolveTemplateRowShopId(row, listShopId), id, { scope: row.scope || scopeTab });
          const item = res?.item || res || {};
          const serverEnabled = item.autoRuleEnabled === true || item.autoRuleEnabled === 1;
          const serverVersion = parseTemplateVersion(item.version);
          setItems((prev) => prev.map((itemRow) => (
            String(itemRow.id) === key
              ? {
                ...itemRow,
                autoRuleEnabled: serverEnabled,
                ...(serverVersion != null ? { version: serverVersion } : {}),
              }
              : itemRow
          )));
        } catch {
          // 单行刷新失败时保持回滚状态
        }
      } else {
        showToast(e?.message || t('productTemplate.autoUpdateFailed'), 'error');
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const emptyHint = !canQuery
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (keyword || styleNo || productTypeId
        ? t('photoLib.noMatch')
        : (isOfficialTab ? t('photoLib.emptyOfficial') : t('productTemplate.empty'))));

  return (
    <>
      <TemuSyncProgressSlot kinds={['photoTemplateBatchCopy']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || deleting || exporting || photoBatchCopying || moving}
            batchCopying={photoBatchCopying}
            batchMoving={moving}
            batchOpsMenu={buildTemuWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || deleting || exporting || isOfficialTab,
            })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!canQuery || loading || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'create',
                label: t('photoLib.create'),
                tone: 'primary',
                disabled: !canQuery || loading || deleting || exporting,
                onClick: async () => {
                  const writeShopId = await ensureWriteShopId({
                    title: t('productTemplate.createPickTitle'),
                    description: t('sizeChart.createPickDesc'),
                    confirmText: t('productTemplate.createHere'),
                  });
                  if (!writeShopId) {
                    showToast(t('returnList.pickShop'), 'warning');
                    return;
                  }
                  setEditingId(null);
                  setEditingShopId(writeShopId);
                  setModalOpen(true);
                },
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--category">
              <span className="tpo-field__label">{t('productTemplate.colCategory')}</span>
              <CategoryCascaderSelect
                shopId={listShopId}
                categories={categories}
                value={categoryIdDraft}
                disabled={!canQuery || loading}
                placeholder={!canQuery ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                emptyText={!canQuery ? t('returnList.pickShop') : t('productTemplate.noCategories')}
                onChange={onCategoryChange}
              />
            </div>
            <TemuStyleNoBatchFilter
              value={styleNoDraft}
              disabled={!canQuery}
              onChange={setStyleNoDraft}
              onEnter={runQuery}
            />
            <TemuTemplateNameBatchFilter
              value={keywordDraft}
              disabled={!canQuery}
              onChange={setKeywordDraft}
              onEnter={runQuery}
            />
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
              disabled={!canQuery || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!canQuery || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(canQuery) && loading}
        loadingLabel={t('productTemplate.querying')}>
        <TemuTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('photoLib.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className={`tpo-publish__table tpo-drafts__table tpo-certs-table tpo-tpl-table tpo-tpl-table--photo${isOfficialTab ? ' tpo-tpl-table--photo-official' : ''}`}>
            <colgroup>
              <col className="tpo-certs-col tpo-certs-col--check" />
              <col className="tpo-certs-col tpo-certs-col--order" />
              <col className="tpo-certs-col tpo-certs-col--preview" />
              <col className="tpo-certs-col tpo-certs-col--style-no" />
              <col className="tpo-certs-col tpo-certs-col--name" />
              <col className="tpo-certs-col tpo-certs-col--category" />
              <col className="tpo-certs-col tpo-certs-col--spu" />
              <col className="tpo-certs-col tpo-certs-col--switch" />
              <col className="tpo-certs-col tpo-certs-col--filled" />
              <col className="tpo-certs-col tpo-certs-col--filled" />
              {!isOfficialTab ? <col className="tpo-certs-col tpo-certs-col--updatedAt" /> : null}
              <col className="tpo-certs-col tpo-certs-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--order">{t('productTemplate.colPriority')}</th>
                <th scope="col" className="tpo-certs-th">{t('photoLib.preview')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colStyleNo')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colName')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colCategory')}</th>
                <th scope="col" className="tpo-certs-th">SPU</th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--switch">{t('productTemplate.colAutoRule')}</th>
                <th scope="col" className="tpo-certs-th">{t('photoLib.packageCount')}</th>
                <th scope="col" className="tpo-certs-th">{t('photoLib.bodyCount')}</th>
                {!isOfficialTab ? <th scope="col" className="tpo-certs-th">{t('productTemplate.colUpdatedAt')}</th> : null}
                <th scope="col" className="tpo-certs-th tpo-certs-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || items.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                const canEdit = rowCanEditScopedTemplate(row);
                const canDelete = rowCanDeleteScopedTemplate(row);
                const canCopy = rowCanCopyScopedTemplate(row);
                const canSelect = canDelete || canCopy;
                const rowShopId = resolveTemplateRowShopId(row, listShopId);
                return (
                  <tr
                    key={key}
                    className={canSelect ? 'tpo-row--clickable' : undefined}
                    onClick={(e) => {
                      if (!canSelect) return;
                      if (e.target.closest('button, a, input, .tpo-copy-hover, .temu-draft-actions')) return;
                      toggleOne(key);
                    }}
                  >
                    <td className="tpo-certs-td tpo-certs-td--check">
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        disabled={!canSelect}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          if (!canSelect) return;
                          toggleOne(key);
                        }}
                      />
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--order">{row.sortOrder ?? 0}</td>
                    <td className="tpo-certs-td">
                      <div className="tpo-template-thumbs">
                        <PreviewThumb
                          src={row.packageThumbUrl || ''}
                          alt={t('photoLib.packageAlt', { name: row.name || t('productTemplate.templateFallback') })}
                          onPreview={(src, alt) => {
                            setPreviewSrc(src);
                            setPreviewAlt(alt || t('photoLib.packagePreview'));
                          }}
                        />
                        <PreviewThumb
                          src={row.bodyThumbUrl || ''}
                          alt={t('photoLib.bodyAlt', { name: row.name || t('productTemplate.templateFallback') })}
                          onPreview={(src, alt) => {
                            setPreviewSrc(src);
                            setPreviewAlt(alt || t('photoLib.bodyPreview'));
                          }}
                        />
                      </div>
                    </td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.styleNo)}>{row.styleNo || '—'}</td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.name, row.remark)}>
                      <strong>{row.name || '—'}</strong>
                      {row.remark ? <div className="tpo-muted">{row.remark}</div> : null}
                    </td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.categoryPathLabel || row.productTypeId)}>
                      {row.categoryPathLabel || row.productTypeId || '—'}
                    </td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.spuTitle || row.spuName)}>
                      {row.spuName ? (
                        <>
                          <div>{row.spuName}</div>
                          {row.spuTitle ? <div className="tpo-muted">{row.spuTitle}</div> : null}
                        </>
                      ) : '—'}
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--switch">
                      <TemuSwitch
                        checked={!!row.autoRuleEnabled}
                        disabled={!canEdit || deleting || togglingIds.has(key)}
                        ariaLabel={t('productTemplate.autoRuleAria', { name: row.name || t('productTemplate.templateFallback') })}
                        onChange={(next) => onToggleAutoRule(row, next)}
                      />
                    </td>
                    <td className="tpo-certs-td">{row.packageCount ?? 0}</td>
                    <td className="tpo-certs-td">{row.bodyCount ?? 0}</td>
                    {!isOfficialTab ? (
                      <td className="tpo-certs-td tpo-certs-td--time" title={cellDisplayTitle(formatBeijingDateTime(row.updatedAt))}>
                        {formatBeijingDateTime(row.updatedAt)}
                      </td>
                    ) : null}
                    <td className="tpo-certs-td tpo-certs-td--ops">
                      <div className="temu-draft-actions">
                        {canEdit ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => {
                              setEditingId(row.id);
                              setEditingShopId(rowShopId);
                              setModalOpen(true);
                            }}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {canCopy ? (
                          <TemuOpsCopyHoverMenu
                            disabled={deleting || copyingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOne(row, target),
                            })}
                          />
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => onDeleteOne(row)}
                          >
                            {t('returnList.delete')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('photoLib.pager')}
        />
      </ProductListCard>

      <PhotoTemplateCreateModal
        open={modalOpen}
        shopId={modalShopId}
        templateId={editingId}
        templateScope={scopeTab}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setEditingShopId('');
        }}
        onSaved={() => {
          setPage(1);
          setQueryNonce((n) => n + 1);
        }}
      />

      <TemuImagePreviewLightbox
        src={previewSrc}
        alt={previewAlt}
        onClose={() => {
          setPreviewSrc('');
          setPreviewAlt('');
        }}
      />
      {shopPickModal}
    </>
  );
}
